import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NextUp } from "@/components/Callouts";

export default function EarTraining1() {
  const { lesson, course, accent } = findLesson("ear-training", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET1-Find-Scale-1-Animated.mp4"
        caption="Finding the scale — overlay the major scale from the home note"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="01" eyebrow="Ear Training · Chapter One" title="Find the key of any song — using only your ears">
        <Prose>
          <p>
            Welcome to the side of music that <em>can't</em> be looked up on a chart. Ear
            training is the long, slow project of teaching your ear to <strong>recognise
            what theory describes</strong>. We'll spend the next ten lessons learning to
            hear scales, intervals, chords, melodies, and rhythm — to take any song that
            comes on the radio and reverse-engineer it without touching a piano.
          </p>
          <p>
            We start with the most useful skill in the world: figuring out the
            <strong> key</strong> of a song you've never seen written down. With this skill
            alone, you can jam over any track, transpose it for a singer, or pull up the
            right chord chart on the spot.
          </p>
        </Prose>

        <MTQuote>
          When you're finding the scale of a song, it should be a journey. It shouldn't be
          like, I have to get it perfectly. You don't have to get it perfectly. You can make
          some mistakes along the way.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The detective method" title="Three things to find — in order">
        <Prose>
          <p>
            Finding a song's key is finding three things, and the order matters. Skip a
            step and the next one becomes guessable rather than findable.
          </p>
        </Prose>

        <Steps>
          <Step><strong>Find the resting note (the tonic).</strong> Listen for the note the song keeps coming home to — usually the last note of the chorus, the held note at the end of a phrase, or the bass under the first chord of the chorus. Hum it, find it on the piano. That's the 1.</Step>
          <Step><strong>Decide major or minor.</strong> From the tonic, hum a major 3rd up (4 semitones). Does that note feel "in" the song? If yes, the key is <em>major</em>. If it sounds wrong and the minor 3rd (3 semitones) feels right instead, the key is <em>minor</em>. The 3rd is the entire diagnostic.</Step>
          <Step><strong>Confirm by humming the scale.</strong> Sing 1 through 7 in the proposed key against the recording. Every degree should fit naturally somewhere in the song. If it does, you've found the key. If a degree fights, you've got the wrong scale — likely a modal flavour (Dorian, Mixolydian) or the relative minor.</Step>
        </Steps>

        <NoteImage
          src="/notes/ear-training/finals/01a-melody-flowchart-overall.png"
          caption="Jason's note: the full melody flowchart — Listen & Sing Along → Key/Scale/Signature → Essential Theory (piano worm, scale circle, triads, cadences) → Melodic Curves → Intervals → Staff Notation with Rhythm. We're at step 1 right now: find the key."
        />
      </Chapter>

      <Chapter eyebrow="The shortcut" title="The chorus usually opens on I">
        <Prose>
          <p>
            Here is a trick worth more than its weight in gold for pop, rock, folk, country
            and most Bollywood songs: the first chord of the chorus is very often the
            <strong>tonic chord (I)</strong>. So if you can identify what root note that
            chord is built on, you usually have the key — instantly, no humming required.
            It is a strong bet rather than a rule: choruses that open on IV, vi or ♭VII are
            common enough that you should always confirm with step 3 below before committing.
          </p>
          <p>
            Scrub to the chorus. Hum the root of the first chord. Find it on the piano. Done.
            Sometimes the first verse chord is the I as well (especially in slow ballads),
            so try the verse opening too. But the chorus first chord is the most reliable
            single signpost in pop music.
          </p>
        </Prose>

        <TheoryBox label="A good first guess">
          A lot of pop sits in keys like <strong>C, G, D, A, E or F major</strong> (and their
          relative minors) — keys that suit guitars, pianos and the comfortable range of most
          untrained voices, which is often not much more than an octave and a bit. So if
          you're stuck, test those keys first. It's a starting point, not a rule — what you
          hear always has the final say.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Try it now" title="Audit three songs you already know">
        <Prose>
          <p>
            Pick three songs you can hum from memory. For each, find the resting note on
            the piano — sing the chorus first, hold the final note, then hunt for it on
            the keyboard. Then test major vs minor with the 3rd. Within an hour you'll have
            three confirmed keys, and the workflow will feel automatic by song five or six.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["major", "naturalMinor"]} />

        <Prose>
          <p>
            Use the explorer above to test your guess. Click on a tonic, listen to all
            seven notes of the major and natural minor, and compare against the song. The
            scale that "fits" is the answer.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="What's next" title="From key to scale signature">
        <Prose>
          <p>
            Knowing the key is half the battle. Next chapter we'll add the second tool:
            <strong> reading the scale signature by ear</strong> — figuring out whether a
            song that sounds "major" is actually pure Ionian, or whether it's a mode
            (Lydian, Mixolydian) that just borrows a note or two. The technique is to
            listen for the 4th, the 7th, and one or two of the most distinctive scale
            degrees that distinguish modes from each other.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/ear-training-2"
        label="Chapter 2 · How to find the Scale of a Song (Part 2)"
        why="With the key found, we'll dig into the modal flavour — is it Ionian, Mixolydian, Dorian, Aeolian? Three notes can change the answer, and the ear can hear them."
      />
    </LessonShell>
  );
}
